<template>
  <b-container>
    <b-row align-h="start" class="mb-4">
      <b-overlay :show="loading" no-wrap fixed />
      <b-col>
        <h1>
          Order #{{ record.number }} |
          {{
            record.fulfillment_center ? record.fulfillment_center.name : null
          }}
        </h1>
      </b-col>
      <b-col class="text-right">
        <saved-notice v-if="saved" />
        <router-link
          v-if="
            record.network_order &&
            record.network_order &&
            hubIds.includes(record.network_order.facility_id)
          "
          router-link
          class="d-inline-block ml-2 mb-1"
          :to="`/orders/${record.network_order.id}`"
          >see full order details ></router-link
        >
        <b-button
          class="mb-1 ml-2"
          variant="primary"
          @click="$router.push('/kitchen-orders')"
        >
          Back
        </b-button>
      </b-col>
    </b-row>
    <b-tabs content-class="mt-3" nav-wrapper-class="nav-wrapper-class" active>
      <b-tab title="Order Details">
        <b-container>
          <b-row v-if="record" class="base-form mb-3">
            <b-col v-if="record.network_order" sm="12" md="6" lg="3">
              <b-form-group label="Status">
                {{ record.status | statusFormat }}
              </b-form-group>
              <b-form-group v-if="record.network_order.facility" label="Hub">
                {{ record.network_order.facility.name }}
              </b-form-group>
              <b-form-group v-if="record.network_order.network" label="Network">
                {{ record.network_order.network.name }}
              </b-form-group>
              <b-form-group label="Fulfillment Method">
                {{
                  record.network_order.fulfillment_type
                    | fulfillmentTypeFormat
                    | capitalize
                }}
              </b-form-group>
            </b-col>
            <b-col v-if="record.network_order" sm="12" md="6" lg="3">
              <b-form-group label="Customer">
                <template v-if="record.network_order.user">
                  {{ record.network_order.user.first_name }}
                  {{ record.network_order.user.last_name }}
                </template>
              </b-form-group>
              <b-form-group label="Email">
                {{ record.network_order.email }}
              </b-form-group>
              <b-form-group label="Phone">
                {{ record.network_order.phone | phone }}
              </b-form-group>
              <b-form-group label="Utensil Quantity">
                {{ record.network_order.utensil_quantity }}
              </b-form-group>
            </b-col>
            <b-col v-if="record.network_order" sm="12" md="6" lg="3">
              <b-form-group label="Order Placed">
                {{ record.network_order.placed | formatDateTime }}
              </b-form-group>
              <b-form-group label="Kitchen Ready">
                {{ record.kitchenReadyTime | formatDateTime }}
              </b-form-group>
              <b-form-group label="Facility Ready Time">
                {{ record.facilityReadyTime | formatDateTime }}
              </b-form-group>
              <b-form-group label="Fulfillment Time">
                {{ record.network_order.fulfillment_time | formatDateTime }}
              </b-form-group>
            </b-col>
            <b-col v-if="record.network_order.address" sm="12" md="6" lg="3">
              <b-form-group v-if="record.network_order" label="Delivery">
                <b-link
                  v-if="isFacilityAdmin"
                  :to="`/deliveries/${record.network_order.delivery.id}`"
                >
                  #{{ record.network_order.delivery.id }}
                </b-link>
                <div v-else>#{{ record.network_order.delivery.id }}</div>
              </b-form-group>
              <b-form-group label="Address">
                {{ record.network_order.address | formatAddressStreet }}
              </b-form-group>
              <b-form-group label="Building">
                {{ record.network_order.address.street }}
              </b-form-group>
              <b-form-group label="Delivery Notes">
                {{
                  record.network_order.notes
                    ? record.network_order.notes
                    : 'None'
                }}
              </b-form-group>
            </b-col>
          </b-row>
          <b-row v-if="record" class="base-form p-2">
            <b-col cols="12" class="pb-0 mb-2">
              <p class="col-form-label">
                {{
                  record.fulfillment_center
                    ? record.fulfillment_center.name
                    : null
                }}
              </p>
            </b-col>
            <b-col
              v-for="orderItem in record.order_items"
              :key="orderItem.id"
              cols="12"
              class="mb-2"
            >
              <b-row no-gutters>
                <b-col cols="12" md="3" class="mb-2 mb-sm-0">
                  <div class="d-flex">
                    <b-select
                      v-model="orderItemStatuses[orderItem.id]"
                      class="mr-0 mr-sm-2"
                      :options="orderStatusOptions"
                      :disabled="readOnly"
                    />
                    <checkbox-button
                      v-if="
                        orderItem.status !== orderItemStatuses[orderItem.id]
                      "
                      :value="true"
                      class="mr-2"
                      @click.native="setOrderItemStatus(orderItem)"
                    />
                  </div>
                </b-col>
                <b-col cols="9" md="7" class="pl-0 pl-sm-3">
                  <b-row class="mt-0 mt-sm-1">
                    <b-col cols="1">{{ orderItem.quantity }}x</b-col>
                    <b-col cols="11">
                      {{ orderItem.name }}
                      <ul
                        v-if="
                          orderItem.order_item_attributes || orderItem.notes
                        "
                        class="mb-0"
                      >
                        <li
                          v-for="orderItemAttribute in orderItem.order_item_attributes"
                          :key="orderItemAttribute.id"
                        >
                          {{ orderItemAttribute.name }} -
                          {{ orderItemAttribute.value }} +
                          {{ (orderItemAttribute.price / 100) | currency }}
                        </li>
                        <li v-if="orderItem.notes">
                          <strong>Special Instructions:</strong>
                          {{ orderItem.notes }}
                        </li>
                      </ul>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col cols="3" md="2" class="text-right">
                  {{ (orderItem.price / 100) | currency }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-container>
      </b-tab>
      <b-tab title="Payments & Refunds">
        <kitchen-order-payment :order="record" />
      </b-tab>
    </b-tabs>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import KitchenOrder from '../../models/KitchenOrder.js';
import kitchenOrderPayment from './KitchenOrderPayment.vue';
import ResourceAPI from '../../services/ResourceAPI';
import SavedNotice from '../../components/SavedNotice';

export default {
  name: 'KitchenOrders',
  components: {
    kitchenOrderPayment,
    SavedNotice,
  },
  data() {
    return {
      loading: true,
      saved: false,
      record: null,
      orderItemStatuses: [],
      orderStatusOptions: [
        {
          value: null,
          text: 'Choose status',
        },
        {
          value: 'placed',
          text: 'Placed',
        },
        {
          value: 'confirmed',
          text: 'Confirmed',
        },
        {
          value: 'preparing',
          text: 'Preparing',
        },
        {
          value: 'prepared',
          text: 'Prepared',
        },
        {
          value: 'enroute_expo',
          text: 'En Route Expo',
        },
        {
          value: 'compiling',
          text: 'Compiling',
        },
        {
          value: 'enroute_delivery_expo',
          text: 'En Route Delivery Expo',
        },
        {
          value: 'delivery_compiling',
          text: 'Delivery Compiling',
        },
        {
          value: 'enroute_dispatch',
          text: 'Enroute Dispatch',
        },
        {
          value: 'ready',
          text: 'Ready',
        },
        {
          value: 'enroute_customer',
          text: 'Enroute',
        },
        {
          value: 'completed',
          text: 'Completed',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['hubIds', 'isFacilityAdmin']),
    readOnly() {
      if (
        this.record &&
        !this.hubIds.includes(this.record.network_order.facility_id)
      ) {
        return true;
      }

      return false;
    },
  },
  created() {
    this.loadRecord();
  },
  methods: {
    loadRecord() {
      const me = this;

      me.loading = true;
      me.record = new KitchenOrder({ id: this.$route.params.id });
      me.record
        .fetch()
        .then(() => {
          me.loading = false;
          me.record.order_items.forEach((orderItem) => {
            me.orderItemStatuses[orderItem.id] = orderItem.status;
          });
        })
        .catch((error) => alert(error));
    },
    setOrderItemStatus(orderItem) {
      const me = this;

      ResourceAPI.makePatchRequest(
        `kms/orders/${me.record.network_order_id}/order-item-status`,
        {
          orderItems: [
            {
              id: orderItem.id,
              status: me.orderItemStatuses[orderItem.id],
            },
          ],
        }
      ).then(() => {
        me.loadRecord();
        me.saved = true;
        setTimeout(() => {
          me.saved = false;
        }, 5000);
      });
    },
  },
};
</script>
